import { DateTime } from 'luxon';
import { getCallOutcomeOptions } from '../../../../tasks/components/tasks-content/utils/helper';
import { CallSource, CallSourceActionType } from '../../../types/call-logs';
import { TeamsAndUsersList } from '../../../../settings/types/users-and-teams';
import { TranscriptItem } from '../../../types';

export const formatDuration = (seconds: number): string => {
  if (seconds === 0) return '0s';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hours > 0) {
    if (minutes === 0 && secs === 0) return `${hours}h`;
    if (secs === 0) return `${hours}h ${minutes}m`;
    if (minutes === 0) return `${hours}h ${secs}s`;
    return `${hours}h ${minutes}m ${secs}s`;
  }
  if (minutes === 0) return `${secs}s`;
  if (secs === 0) return `${minutes}m`;
  return `${minutes}m ${secs}s`;
};

export const formatTime = (ms: number) => {
  const totalSeconds = Math.floor(ms / 1000);

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${String(hours).padStart(2, '0')} : ${String(minutes).padStart(
      2,
      '0',
    )}:${String(seconds).padStart(2, '0')}`;
  }

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
    2,
    '0',
  )}`;
};

export const formatDateTime = (isoString: string): string => {
  const date = new Date(isoString);
  const day = date.getDate();
  const suffix = getOrdinalSuffix(day);
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear();
  const time = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  return `${day}${suffix} ${month} ${year}, ${time}`;
};

const getOrdinalSuffix = (day: number): string => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};
type FilterOption = { id: string; name: string };

export const formatDateToISO = (date: Date | null): string => {
  if (!date) return '';
  return DateTime.fromJSDate(date).toISODate() || '';
};

/**
 * Formats date range for API: start of day and end of day in user's timezone.
 * callDateStart: "2026-01-11T00:00:00.000+05:30"
 * callDateEnd: "2026-01-11T23:59:59.999+05:30"
 * When only startDate is selected (range incomplete), returns callDateEnd: '' so
 * the DateFilter stays in "waiting for end date" mode and user can complete the range.
 */
export const formatCallDateRangeToISO = (
  startDate: Date | null,
  endDate: Date | null,
  timeZone?: string,
): { callDateStart: string; callDateEnd: string } => {
  if (!startDate) {
    return { callDateStart: '', callDateEnd: '' };
  }
  if (!endDate) {
    // Partial selection: only start chosen — store start so picker shows it, leave end empty so user can select range end
    const startDt = timeZone
      ? DateTime.fromJSDate(startDate).setZone(timeZone)
      : DateTime.fromJSDate(startDate);
    const processedStart = startDt.set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    });
    return {
      callDateStart: processedStart.toISO() || '',
      callDateEnd: '',
    };
  }
  const startDt = timeZone
    ? DateTime.fromJSDate(startDate).setZone(timeZone)
    : DateTime.fromJSDate(startDate);
  const endDt = timeZone
    ? DateTime.fromJSDate(endDate).setZone(timeZone)
    : DateTime.fromJSDate(endDate);
  const processedStart = startDt.set({
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  });
  const processedEnd = endDt.set({
    hour: 23,
    minute: 59,
    second: 59,
    millisecond: 999,
  });
  return {
    callDateStart: processedStart.toISO() || '',
    callDateEnd: processedEnd.toISO() || '',
  };
};

/**
 * Maps call outcome list from API to filter options.
 * Uses id for selection (API expects callOutcomeIds: number[]), name for display.
 */
export const getCallOutcomeOptionsFromLogs = (list: any[]): FilterOption[] => {
  if (!list?.length) return [];
  return list
    .filter((item) => item?.id != null && item?.name)
    .map((item) => ({
      id: String(item.id),
      name:
        typeof item.name === 'string' ? item.name.trim() : String(item.name),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

export const callerHostOptions = (teamsAndUsersList: TeamsAndUsersList[]) => {
  const seen = new Set<string>();
  const options: { id: string; name: string }[] = [];
  (teamsAndUsersList ?? []).forEach((team) => {
    (team.users ?? []).forEach((user) => {
      const id = String(user.id);
      const name = (user.userName || '').trim();
      if (id && name && !seen.has(id)) {
        seen.add(id);
        options.push({ id, name });
      }
    });
  });
  return options?.sort((a, b) => a?.name?.localeCompare(b?.name));
};

export const getAudioExtension = (fileName: string): string => {
  const trimmed = (fileName || '').trim();
  if (!trimmed) return 'voicemail.mp3';
  if (/\.(mp3|wav)$/i.test(trimmed)) return trimmed;
  return `${trimmed}.mp3`;
};

export const parseCallTranscriptionToItems = (
  transcription: string | null | undefined,
): TranscriptItem[] => {
  if (transcription == null || typeof transcription !== 'string') {
    return [];
  }
  const trimmed = transcription.trim();
  if (!trimmed) {
    return [];
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (typeof parsed === 'string') {
      return [
        {
          role: 'CUSTOMER',
          content: parsed,
          startTime: 0,
          sentiment: 'NEUTRAL',
          id: 0,
          name: '',
        },
      ];
    }
    if (Array.isArray(parsed) && parsed.length > 0) {
      const first = parsed[0];
      if (
        first &&
        typeof first === 'object' &&
        !Array.isArray(first) &&
        ('content' in first || 'role' in first)
      ) {
        return parsed.map((item: Record<string, unknown>, index: number) => ({
          role: item?.role === 'AGENT' ? 'AGENT' : 'CUSTOMER',
          content: String(item?.content ?? ''),
          startTime: Number(item?.startTime) || 0,
          sentiment:
            item?.sentiment === 'POSITIVE' || item?.sentiment === 'NEGATIVE'
              ? (item.sentiment as 'POSITIVE' | 'NEGATIVE')
              : 'NEUTRAL',
          id: typeof item?.id === 'number' ? item.id : index,
          name: String(item?.name ?? ''),
        }));
      }
    }
  } catch {
    /* Not JSON — use full string as plain transcription below */
  }

  return [
    {
      role: 'CUSTOMER',
      content: trimmed,
      startTime: 0,
      sentiment: 'NEUTRAL',
      id: 0,
      name: '',
    },
  ];
};

/**
 * Plain text matching the on-screen transcript (speaker • timestamp, then line).
 * Single plain-text block (no structured segments) returns content only.
 */
export const formatTranscriptionItemsAsPlainText = (
  items: TranscriptItem[],
): string => {
  if (!items?.length) return '';
  const first = items[0];
  if (
    items.length === 1 &&
    !String(first?.name ?? '').trim() &&
    (first?.startTime ?? 0) === 0
  ) {
    return String(first?.content ?? '').trim();
  }
  return items
    .map((item) => {
      const name = String(item?.name ?? '').trim();
      const timeLabel = formatTime(item?.startTime ?? 0);
      const header = name ? `${name} • ${timeLabel}` : timeLabel;
      return `${header}\n${String(item?.content ?? '').trim()}`;
    })
    .join('\n\n');
};
